import { z } from 'zod'
import { permissionSchema, type Permission } from './enums.js'
import { lenientUuidSchema, timestampSchema } from './common.js'

// Stable UUIDs for the seeded roles. The migration/seed inserts these rows
// verbatim; tests and any future seed data should import these constants
// rather than looking up by name (admins can rename "Viewer" without
// breaking references). Distinctive all-hex repeats for grep-ability.
export const SEEDED_ROLE_IDS = {
  none: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaa1',
  viewer: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaa2',
  editor: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaa3',
  admin: 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaa5',
} as const

const VIEWER_PERMISSIONS = [
  'users:read',
  'dispatch:read',
] as const satisfies readonly Permission[]
const EDITOR_PERMISSIONS = [
  ...VIEWER_PERMISSIONS,
  'users:write',
  'dispatch:write',
] as const satisfies readonly Permission[]

// Permission sets the seed assigns to each role. Kept here (not in the
// migration SQL) so tests and the seed script reuse the same mapping when
// constructing fixture users.
export const SEEDED_ROLE_PERMISSIONS: Record<
  keyof typeof SEEDED_ROLE_IDS,
  readonly Permission[]
> = {
  none: [],
  viewer: VIEWER_PERMISSIONS,
  editor: EDITOR_PERMISSIONS,
  admin: ['admin'],
}

// Used by `wouldLeaveZeroAdmins` on the server. A role grants admin access by
// holding the master `admin` permission.
export const ADMIN_ROLE_ID = SEEDED_ROLE_IDS.admin

export const roleSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(64),
  description: z.string().max(200),
  permissions: z.array(permissionSchema),
  // Seeded roles are immutable in name and id (their permissions can still be
  // edited by an admin, subject to the lockout guard). The UI uses this flag
  // to disable the rename input and the delete button.
  system: z.boolean(),
  // Admin-controlled global display order. Lower = earlier.
  sortOrder: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})
export type Role = z.infer<typeof roleSchema>

// Lightweight projection used wherever a role is embedded (e.g. on a User).
export const roleRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  system: z.boolean(),
})
export type RoleRef = z.infer<typeof roleRefSchema>

export const createRoleInputSchema = z.object({
  name: z.string().min(1).max(64),
  description: z.string().max(200).default(''),
  permissions: z.array(permissionSchema),
})
export type CreateRoleInput = z.infer<typeof createRoleInputSchema>

export const updateRoleInputSchema = z.object({
  name: z.string().min(1).max(64).optional(),
  description: z.string().max(200).optional(),
  permissions: z.array(permissionSchema).optional(),
})
export type UpdateRoleInput = z.infer<typeof updateRoleInputSchema>

// Body shape for reordering roles. The full ordered id list is required on
// every call — partial reorders aren't supported because two concurrent
// partial PATCHes could leave gaps or duplicate positions.
export const reorderRolesInputSchema = z.object({
  orderedIds: z.array(lenientUuidSchema).min(1),
})
export type ReorderRolesInput = z.infer<typeof reorderRolesInputSchema>
